import React, { useEffect, useState } from 'react'
import {
    generateDaysInMonth,
    goNext,
    goPrev,
    goToday,
    handleDayChange,
    handleMonthChange,
    handleYearChange,
    setView,
} from './../../../utils/calendar-utils';
import {
    Check,
    ChevronLeft,
    ChevronRight,
    ChevronsUpDown,
    GalleryVertical,
    Table,
    Tally3,
} from 'lucide-react';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './../../../components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './../../../components/ui/popover';
import { cn } from './../../../lib/utils'
import { Input } from './../../../components/ui/input';
import { Button } from './../../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from './../../../components/ui/tabs';

function CalendarNav({ viewedDate, calendarRef, startDate, endDate, setViewedDate }) {
    const [currentView, setCurrentView] = useState('timeGridWeek');
    const [daySelectOpen, setDaySelectOpen] = useState(false);
    const [monthSelectOpen, setMonthSelectOpen] = useState(false);

    const selectedMonth = viewedDate.getMonth() + 1;
    const selectedDay = viewedDate.getDate();
    const selectedYear = viewedDate.getFullYear();
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const dayOptions = generateDaysInMonth(daysInMonth);

    useEffect(() => {
        if (startDate && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(new Date(startDate));
            setViewedDate(new Date(startDate));
        }
    }, [startDate, endDate]);

    const months = [
        {
            value: '1',
            label: 'January',
        },
        {
            value: '2',
            label: 'February',
        },
        {
            value: '3',
            label: 'March',
        },
        {
            value: '4',
            label: 'April',
        },
        {
            value: '5',
            label: 'May',
        },
        {
            value: '6',
            label: 'June',
        },
        {
            value: '7',
            label: 'July',
        },
        {
            value: '8',
            label: 'August',
        },
        {
            value: '9',
            label: 'September',
        },
        {
            value: '10',
            label: 'October',
        },
        {
            value: '11',
            label: 'November',
        },
        {
            value: '12',
            label: 'December',
        },
    ];
    return (
        <div className="flex min-w-full flex-col items-center justify-center gap-3 px-10 md:flex-row">
            {/* Navigation Controls */}
            <div className="flex space-x-1">
                <Button
                    variant="ghost"
                    className="w-8"
                    onClick={() => goPrev(calendarRef)}
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Month Selector */}
                <Popover open={monthSelectOpen} onOpenChange={setMonthSelectOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            className="w-[105px] justify-between text-xs font-semibold"
                        >
                            {months?.find(
                                (month) => month.value === String(selectedMonth)
                            )?.label || 'Select month...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0">
                        <Command>
                            <CommandInput placeholder="Search month..." />
                            <CommandList>
                                <CommandEmpty>No month found.</CommandEmpty>
                                <CommandGroup>
                                    {months.map((month) => (
                                        <CommandItem
                                            key={month.value}
                                            value={month.value}
                                            onSelect={(currentValue) => {
                                                handleMonthChange(
                                                    calendarRef,
                                                    viewedDate,
                                                    currentValue
                                                );
                                                setMonthSelectOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    String(selectedMonth) === month.value
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            {month.label}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {/* Year Input */}
                <Input
                    className="w-[75px] text-xs font-semibold"
                    type="number"
                    value={selectedYear}
                    onChange={(e) =>
                        handleYearChange(calendarRef, viewedDate, e.target.value)
                    }
                />

                <Button
                    variant="ghost"
                    className="w-8"
                    onClick={() => goNext(calendarRef)}
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-center gap-3">
                <Button
                    className="w-[90px] text-xs"
                    variant="outline"
                    onClick={() => goToday(calendarRef)}
                >
                    {currentView === 'timeGridDay'
                        ? 'Today'
                        : currentView === 'timeGridWeek'
                            ? 'This Week'
                            : 'This Month'}
                </Button>

                {/* View Selector */}
                <Tabs defaultValue="dayGridMonth">
                    <TabsList className="flex w-44 md:w-64">
                        <TabsTrigger
                            value="timeGridWeek"
                            onClick={() =>
                                setView(calendarRef, 'timeGridWeek', setCurrentView)
                            }
                            className={currentView === 'timeGridWeek' ? 'w-1/2' : 'w-1/4'}
                        >
                            <Tally3 className="h-5 w-5" />
                            {currentView === 'timeGridWeek' && (
                                <div className="text-xs">Week</div>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="dayGridMonth"
                            onClick={() =>
                                setView(calendarRef, 'dayGridMonth', setCurrentView)
                            }
                            className={currentView === 'dayGridMonth' ? 'w-1/2' : 'w-1/4'}
                        >
                            <Table className="h-5 w-5 rotate-90" />
                            {currentView === 'dayGridMonth' && (

                                <div className='text-xs'>Month</div>
                            )}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
        </div>
    )
}

export default CalendarNav